import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';
import { Search, UserPlus, MessageCircle, Phone, Mail, Trash2, ChevronUp, ChevronDown, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight, Loader, AlertTriangle, AlertCircle, Upload } from 'lucide-react';
import { Skeleton, Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem, Toast } from '../ui';
import { getWhatsappLink, formatDate } from '../../utils/helpers';
import { ContactModal } from '../modals';
import { useLanguage } from '../../contexts/LanguageContext';
import CsvSetupModal from '../modals/CsvSetupModal';
import CsvImportModal from '../modals/CsvImportModal';
import { useCsvUpload } from '../../hooks/useCsvUpload';
import { sourcesService } from '../../services/api';

export default function ContactsView({ contacts, loading, onAddContact, onUpdateContact, onDeleteContact, onDeleteAllContacts, searchContacts, fetchAllContacts, pagination }) {
  const { t } = useLanguage();
  // URL State
  const [searchParams, setSearchParams] = useSearchParams();
  const showModal = searchParams.get('modal') === 'new-contact';

  const selectedContactId = searchParams.get('contactId');
  const isEditing = searchParams.get('modal') === 'edit-contact'

  const selectedContact = useMemo(() => {
    if (!isEditing || !selectedContactId) return null;
    return contacts.find(c => c.id == selectedContactId) || null;
  }, [contacts, selectedContactId, isEditing]);

  const setShowModal = (show) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (show) newParams.set('modal', 'new-contact');
      else if (newParams.get('modal') === 'new-contact') newParams.delete('modal');
      return newParams;
    });
  };

  const setSelectedContact = (contact) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (contact) {
        newParams.set('modal', 'edit-contact');
        newParams.set('contactId', contact.id);
      } else {
        if (newParams.get('modal') === 'edit-contact') newParams.delete('modal');
        newParams.delete('contactId');
      }
      return newParams;
    });
  };
  const [globalFilter, setGlobalFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('All');
  const [sorting, setSorting] = useState([]);
  const [toast, setToast] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showImportSetup, setShowImportSetup] = useState(false);
  const [{ pageIndex, pageSize }, setPaginationState] = useState({
    pageIndex: 0,
    pageSize: parseInt(localStorage.getItem('contactsPageSize')) || 10,
  });

  const [dbSources, setDbSources] = useState([]);

  React.useEffect(() => {
    localStorage.setItem('contactsPageSize', pageSize);
  }, [pageSize]);

  React.useEffect(() => {
    sourcesService.getAll()
      .then(res => {
        if (Array.isArray(res)) {
          setDbSources(res.map(s => s.name).filter(Boolean));
        }
      })
      .catch(err => console.error('Error fetching sources for contacts filter:', err));
  }, []);

  const { importProgress, closeImportModal, handleFileUpload } = useCsvUpload('contacts', () => {
    if (fetchAllContacts) fetchAllContacts({ page: pageIndex + 1, limit: pageSize, search: globalFilter, source: sourceFilter });
  });

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    
    if (confirmDelete.type === 'single') {
        try {
            if (onDeleteContact) await onDeleteContact(confirmDelete.id);
            setToast({ message: t('contactDeleted') || 'Contact deleted successfully.', type: 'success' });
        } catch (error) {
            setToast({ message: error.response?.data?.message || error.message || 'Failed to delete contact', type: 'error' });
        }
    } else if (confirmDelete.type === 'all') {
        try {
            await onDeleteAllContacts();
            setToast({ message: t('allContactsDeleted') || 'All contacts deleted successfully.', type: 'success' });
        } catch (error) {
            setToast({ message: error.response?.data?.message || error.message || 'Failed to delete contacts', type: 'error' });
        }
    }
    setConfirmDelete(null);
  };


  // Server-side Pagination/Search Trigger
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (fetchAllContacts) {
        fetchAllContacts({
          page: pageIndex + 1,
          limit: pageSize,
          search: globalFilter,
          source: sourceFilter
        });
      }
    }, 400);
  }, [globalFilter, sourceFilter, pageIndex, pageSize, fetchAllContacts]);

  const availableSources = useMemo(() => {
    const dynamicSources = (contacts || []).map(c => c.source).filter(Boolean);
    return Array.from(new Set([...dbSources, 'Manual Entry', ...dynamicSources]));
  }, [dbSources, contacts]);

  // Define columns for the table
  const columns = useMemo(() => [
    {
      id: 'name',
      accessorFn: row => `${row.firstName} ${row.lastName || ''}`.trim(),
      header: t('name'),
      cell: ({ row }) => {
        const displayName = `${row.original.firstName || ''} ${row.original.lastName || ''}`.trim();
        return (
          <button
            onClick={() => setSelectedContact(row.original)}
            className="font-semibold text-blue-600 hover:text-blue-800 hover:underline text-left whitespace-nowrap"
          >
            {displayName}
          </button>
        );
      },
    },
    {
      accessorKey: 'phone',
      header: t('phone'),
      cell: ({ getValue }) => (
        <span className="font-mono text-gray-600 whitespace-nowrap">{getValue()}</span>
      ),
    },
    {
      accessorKey: 'email',
      header: t('email'),
      cell: ({ getValue }) => (
        <span className="text-gray-500 truncate max-w-[180px] block">{getValue() || '-'}</span>
      ),
    },
    {
      accessorKey: 'source',
      header: t('source') || 'Source',
      cell: ({ getValue }) => {
        const source = getValue() || 'Manual Entry';
        const isMeta = source.toLowerCase().includes('meta') || source.toLowerCase().includes('facebook');
        const isCsv = source.toLowerCase().includes('csv') || source.toLowerCase().includes('import');
        const isWa = source.toLowerCase().includes('whatsapp');
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${
            isMeta ? 'bg-blue-100 text-blue-800' :
            isCsv ? 'bg-purple-100 text-purple-800' :
            isWa ? 'bg-green-100 text-green-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {source}
          </span>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: t('created'),
      cell: ({ getValue }) => {
        const date = getValue();
        if (!date) return '-';
        return (
          <span className="text-gray-500 whitespace-nowrap">
            {formatDate(date, {
              day: '2-digit',
              month: 'short',
              year: 'numeric'
            })}
          </span>
        );
      },
    },
    {
      id: 'actions',
      header: t('actions'),
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => window.open(getWhatsappLink(row.original.phone), '_blank')}
            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
            title={t('whatsapp')}
          >
            <MessageCircle className="w-4 h-4" />
          </button>
          <button
            onClick={() => setConfirmDelete({ type: 'single', id: row.original.id })}
            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            title={t('delete')}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
      enableSorting: false,
    },
  ], [onDeleteContact, t]);

  // Initialize the table
  const table = useReactTable({
    data: contacts,
    columns,
    pageCount: pagination?.totalPages || -1,
    state: {
      globalFilter,
      sorting,
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    manualPagination: true,
    onPaginationChange: setPaginationState,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const handleSave = async (data) => {
    await onAddContact(data);
    setShowModal(false);
  };

  const handleUpdate = async (data) => {
    if (selectedContact && selectedContact.id) {
      await onUpdateContact(selectedContact.id, data);
    }
    setSelectedContact(null);
  };

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Add New Contact Modal */}
      {showModal && <ContactModal onClose={() => setShowModal(false)} onSave={handleSave} />}

      {/* Edit Contact Modal */}
      {selectedContact && (
        <ContactModal
          onClose={() => setSelectedContact(null)}
          onSave={handleUpdate}
          initialData={selectedContact}
        />
      )}

      {/* Header */}
      <div className="mb-4">
        <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">{t('contactsDirectory')}</h2>
        <p className="text-gray-500 mt-1">{(pagination?.absoluteTotal ?? pagination?.total) || 0} {t('totalContacts')}</p>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col lg:flex-row justify-between gap-3 bg-white p-4 md:p-5 rounded-xl border border-gray-200 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full lg:w-auto">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              placeholder={t('searchContacts')}
              value={globalFilter ?? ''}
              onChange={(e) => setGlobalFilter(e.target.value)}
            />
          </div>
          <Dropdown className="w-full sm:w-auto">
            <DropdownTrigger>
              <button
                className="w-full sm:w-auto min-w-[150px] bg-white hover:bg-slate-50 border border-gray-200 rounded-lg text-sm px-3.5 h-[42px] focus:ring-2 focus:ring-blue-500 outline-none flex justify-between items-center text-left shadow-sm transition-colors font-medium text-gray-700"
              >
                <span className="truncate">{sourceFilter === 'All' ? 'All Sources' : sourceFilter}</span>
                <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
              </button>
            </DropdownTrigger>
            <DropdownPopover className="w-[180px] max-h-60 overflow-y-auto">
              <DropdownMenu
                selectedKeys={new Set([sourceFilter])}
                selectionMode="single"
                onSelectionChange={(keys) => {
                  setSourceFilter(Array.from(keys)[0]);
                  setPaginationState(prev => ({ ...prev, pageIndex: 0 }));
                }}
              >
                <DropdownItem id="All">All Sources</DropdownItem>
                {availableSources.map((src) => (
                  <DropdownItem key={src} id={src}>
                    {src}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </DropdownPopover>
          </Dropdown>
        </div>
        <div className="flex flex-wrap sm:flex-nowrap gap-2 sm:gap-3 w-full lg:w-auto">
          <button
            onClick={() => setShowImportSetup(true)}
            className="flex-1 sm:flex-none justify-center h-[42px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 sm:px-4 rounded-lg text-sm font-semibold flex items-center gap-2 hover:bg-emerald-100 transition-colors whitespace-nowrap shadow-sm"
          >
            <Upload className="w-4 h-4" /> <span>{t('importCsv')}</span>
          </button>
          {onDeleteAllContacts && (
            <button
              onClick={() => setConfirmDelete({ type: 'all' })}
              className="flex-none justify-center h-[42px] bg-red-600 text-white px-3 sm:px-4 rounded-lg text-sm font-semibold flex items-center gap-1.5 hover:bg-red-700 transition-colors whitespace-nowrap shadow-sm"
              title={t('deleteAll')}
            >
              <Trash2 className="w-4 h-4" />
              <span className="hidden sm:inline">{t('deleteAll')}</span>
            </button>
          )}
          <button
            onClick={() => setShowModal(true)}
            className="w-full sm:w-auto flex-1 sm:flex-none justify-center h-[42px] bg-blue-600 text-white px-4 rounded-lg text-sm font-semibold flex items-center gap-2 hover:bg-blue-700 transition-colors whitespace-nowrap shadow-sm"
          >
            <UserPlus className="w-4 h-4" />
            <span>{t('addContact')}</span>
          </button>
        </div>
      </div>
      {/* Mobile Card View */}
      <div className={`lg:hidden space-y-2 ${loading ? 'opacity-70 pointer-events-none' : ''}`}>
        {loading && contacts.length === 0 ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm space-y-3">
                <div className="flex justify-between items-start">
                  <div className="space-y-2">
                    <Skeleton width={120} height={16} animation="wave" />
                    <Skeleton width={100} height={12} animation="wave" />
                  </div>
                </div>
                <div className="pt-3 border-t flex justify-between">
                  <Skeleton width={80} height={14} animation="wave" />
                  <Skeleton width={60} height={14} animation="wave" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          table.getRowModel().rows.map((row) => (
            <div
              key={row.id}
              onClick={() => setSelectedContact(row.original)}
              className="bg-white p-4 rounded-xl border shadow-sm active:bg-gray-50 cursor-pointer"
            >
              <div className="flex justify-between items-start">
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-gray-900">
                    {`${row.original.firstName || ''} ${row.original.lastName || ''}`.trim()}
                  </p>
                  <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                    <Phone className="w-3 h-3" /> {row.original.phone}
                  </p>
                  {row.original.email && (
                    <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5 truncate">
                      <Mail className="w-3 h-3" /> {row.original.email}
                    </p>
                  )}
                  <div className="mt-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">
                      {row.original.source || 'Manual Entry'}
                    </span>
                  </div>
                </div>
                <a
                  href={getWhatsappLink(row.original.phone)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-green-100 text-green-600 rounded-full"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MessageCircle className="w-5 h-5" />
                </a>
              </div>
            </div>
          ))
        )}
        {!loading && table.getRowModel().rows.length === 0 && (
          <div className="text-center py-8 text-gray-400">{t('noContacts')}</div>
        )}
      </div>

      {/* Desktop Table View */}
      <div className={`hidden lg:block bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden ${loading ? 'opacity-70 pointer-events-none' : ''}`}>
        {loading && contacts.length === 0 ? (
          <div className="p-4 space-y-4">
            <div className="flex items-center gap-4 border-b pb-3">
              {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} width={100} height={16} animation="wave" />)}
            </div>
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-4 py-2 border-b last:border-0">
                <Skeleton width={150} height={16} animation="wave" />
                <Skeleton width={150} height={14} animation="wave" />
                <Skeleton width={100} height={14} animation="wave" />
                <Skeleton width={80} height={14} animation="wave" />
                <Skeleton width={80} height={14} animation="wave" />
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-200">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className={`px-4 py-3.5 text-left font-semibold text-gray-600 whitespace-nowrap ${header.column.getCanSort() ? 'cursor-pointer select-none hover:bg-gray-100 transition-colors' : ''
                          }`}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <div className="flex items-center gap-2">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() && (
                            <span className="text-gray-400">
                              {header.column.getIsSorted() === 'asc' ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : header.column.getIsSorted() === 'desc' ? (
                                <ChevronDown className="w-4 h-4" />
                              ) : (
                                <div className="w-4 h-4 opacity-0 group-hover:opacity-50">
                                  <ChevronUp className="w-4 h-4" />
                                </div>
                              )}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-100">
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="hover:bg-blue-50/50 transition-colors">
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-4 py-3.5">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Empty State */}
        {!loading && table.getRowModel().rows.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <UserPlus className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">{t('noContacts')}</p>
            <p className="text-sm mt-1">{t('addFirstContact')}</p>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {
        contacts.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
            {/* Page Size Selector */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">{t('show')}</span>
              <Dropdown>
                <DropdownTrigger>
                  <button className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex justify-between items-center w-[70px]">
                    {table.getState().pagination.pageSize}
                    <ChevronDown className="w-3 h-3 text-gray-400 shrink-0 ml-1" />
                  </button>
                </DropdownTrigger>
                <DropdownPopover className="w-[70px]" placement="top">
                  <DropdownMenu
                    selectedKeys={new Set([table.getState().pagination.pageSize.toString()])}
                    selectionMode="single"
                    onSelectionChange={(keys) => table.setPageSize(Number(Array.from(keys)[0]))}
                  >
                    {[10, 20, 50].map((pageSize) => (
                      <DropdownItem key={pageSize} id={pageSize.toString()}>
                        {pageSize}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </DropdownPopover>
              </Dropdown>
              <span className="text-sm text-gray-600">{t('perPage')}</span>
            </div>

            {/* Page Info */}
            <div className="text-sm text-gray-600">
              {t('page')}{' '}
              <span className="font-semibold text-gray-900">
                {table.getState().pagination.pageIndex + 1}
              </span>{' '}
              {t('of')}{' '}
              <span className="font-semibold text-gray-900">
                {table.getPageCount()}
              </span>
              {' · '}
              <span className="text-gray-500">
                {pagination?.total || 0} {t('results')}
              </span>
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="First page"
              >
                <ChevronsLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Previous page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {/* Page Number Buttons */}
              <div className="hidden sm:flex items-center gap-1 mx-2">
                {Array.from({ length: Math.min(5, table.getPageCount()) }, (_, i) => {
                  const currentPage = table.getState().pagination.pageIndex;
                  const totalPages = table.getPageCount();
                  let pageNum;

                  if (totalPages <= 5) {
                    pageNum = i;
                  } else if (currentPage <= 2) {
                    pageNum = i;
                  } else if (currentPage >= totalPages - 3) {
                    pageNum = totalPages - 5 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => table.setPageIndex(pageNum)}
                      className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${currentPage === pageNum
                        ? 'bg-blue-600 text-white'
                        : 'border border-gray-200 hover:bg-gray-50 text-gray-700'
                        }`}
                    >
                      {pageNum + 1}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Next page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Last page"
              >
                <ChevronsRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )
      }



      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      {/* Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center animate-scale-up">
            <div className="w-16 h-16 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {confirmDelete.type === 'all' ? t('confirmDeleteAllTitle') || 'Delete All Contacts?' : t('confirmDeleteTitle') || 'Delete Contact?'}
            </h3>
            <p className="text-gray-500 text-sm mb-4">
              {confirmDelete.type === 'all' 
                ? t('confirmDeleteAll') || 'Are you sure you want to delete all contacts? This action cannot be undone.'
                : t('confirmDelete') || 'Are you sure you want to delete this contact? This action cannot be undone.'}
            </p>
            <div className="bg-orange-50 border border-orange-100 rounded-lg p-3 flex gap-2 items-start text-left mb-6">
              <AlertCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
              <p className="text-orange-700 text-xs leading-relaxed">
                {t('confirmDeleteWarning') || 'Note: Contacts linked to existing leads will not be deleted.'}
              </p>
            </div>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-5 py-2.5 rounded-xl font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-5 py-2.5 rounded-xl font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors shadow-sm shadow-red-200"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSV Modals */}
      <CsvSetupModal 
        isOpen={showImportSetup}
        onClose={() => setShowImportSetup(false)}
        onStartImport={(file) => {
          setShowImportSetup(false);
          handleFileUpload({ target: { files: [file], value: '' } });
        }}
        importType="contacts"
      />

      {importProgress.open && (
        <CsvImportModal progress={importProgress} onClose={closeImportModal} importType="contacts" />
      )}
    </div >
  );
}
